import React, { useEffect, useMemo, useState } from "react";
import { Stack, Typography, Box, Button, Modal, Slide, IconButton } from "@mui/material";
import * as Yup from "yup";
import { Formik } from "formik";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as GlobalIcon } from "../../../../vendor/_new/icon/services/global.svg";

import colors from "../../../../utils/colors";
import { SafeTextField } from "../../../common/Greens";
import useSite from "../../../../hooks/user/useSite";
import { CancelButton, containedButtonSx } from "../../../common/Button";
import { nameField } from "../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../super/design";

const ProjectModalDrawer = ({
  open,
  handleClose,
  baselineKey,
  projectToEdit,
  values,
  isSubmitting,
  errors,
  saveError,
  onClearSaveError,
  handleBlur,
  handleChange,
  handleSubmit,
  isValid,
  touched,
}) => {
  const formSnapshot = useMemo(() => ({ name: values.name }), [values.name]);
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, {
    disabled: isSubmitting,
  });

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          },
        }}
      >
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              height: "100vh",
              width: "33%",
              minWidth: 480,
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GlobalIcon sx={{ width: 28, height: 28, color: "#6371F7" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: "medium", color: colors.neutral[1000] }}>
                    {projectToEdit ? "Edit project" : "Add project"}
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[600] }}>
                    Create a project.
                  </Typography>
                </Box>
              </Stack>

              <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
                <CloseIcon sx={{ color: colors.neutral[600] }} />
              </IconButton>
            </Stack>
            <form noValidate onSubmit={handleSubmit}>
              <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
                <ReactPerfectScrollbar>
                  <Stack spacing={4} justifyContent={"space-between"} sx={{ height: "100%", pr: 4 }}>
                    <Stack spacing={4}>
                      {!!errors?.submit?.message && (
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          spacing={2}
                          sx={{
                            minHeight: 48,
                            p: 2,
                            borderRadius: "8px",
                            backgroundColor: colors.red[10],
                            border: `solid 1px ${colors.red[100]}`,
                          }}
                        >
                          <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.red[100] }}>
                            {errors?.submit?.message}
                          </Typography>
                        </Stack>
                      )}
                      <SafeTextField
                        size="small"
                        name="name"
                        label="Project name"
                        value={values.name}
                        error={Boolean((touched.name && errors.name) || saveError)}
                        helperText={(touched.name && errors.name) || saveError || ""}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          if (saveError) onClearSaveError?.();
                          handleChange(e);
                        }}
                      />
                      <Stack
                        direction={"row"}
                        spacing={2}
                        sx={{
                          minHeight: 48,
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: colors.primary["alpha"],
                          border: `solid 1px ${colors.primary[300]}`,
                        }}
                      >
                        <Box sx={{ width: 24, height: 24 }}>
                          <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                        </Box>
                        <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                          You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack direction={"row"} alignItems={"center"} spacing={3}>
                      <Button type="submit" disabled={isSubmitting || !isValid} sx={{ height: 44, width: "50%", ...containedButtonSx }}>
                        Save
                      </Button>
                      <CancelButton onClick={requestClose} />
                    </Stack>
                  </Stack>
                </ReactPerfectScrollbar>
              </Box>
            </form>
          </Stack>
        </Slide>
      </Modal>
      {confirmDialog}
    </>
  );
};

const ProjectModal = ({ open, projectToEdit, handleClose, getProjectList }) => {
  const { addProject, updateProject } = useSite();
  const [baselineKey, setBaselineKey] = useState(0);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (open) {
      setBaselineKey((k) => k + 1);
      setSaveError("");
    }
  }, [open, projectToEdit]);

  return (
    <Formik
      enableReinitialize={true}
      validateOnMount
      validateOnChange
      initialValues={{
        name: projectToEdit ? projectToEdit?.name : "",
      }}
      validationSchema={Yup.object().shape({
        name: nameField,
      })}
      onSubmit={async (values, { setStatus, setSubmitting }) => {
        try {
          setSaveError("");
          if (projectToEdit) {
            await updateProject(projectToEdit?.id, values);
          } else {
            await addProject(values);
          }
          await getProjectList();
          setStatus({ success: true });
          setSubmitting(false);
          handleClose();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setSaveError(message);
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, isValid, touched, values }) => (
        <ProjectModalDrawer
          open={open}
          handleClose={handleClose}
          baselineKey={baselineKey}
          projectToEdit={projectToEdit}
          values={values}
          isSubmitting={isSubmitting}
          errors={errors}
          saveError={saveError}
          onClearSaveError={() => setSaveError("")}
          handleBlur={handleBlur}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          isValid={isValid}
          touched={touched}
        />
      )}
    </Formik>
  );
};
export default ProjectModal;
